import { useState } from "react";
import { FONT, C, CheckIcon } from "../lib/programConstants";

export default function ProgramDashboardStep({ programData = null, onLogout }) {
  const p = programData;
  const [expandedWeek, setExpandedWeek] = useState(p?.currentWeek || 1);
  const isUnanchored = p?.schedulingMode === "unanchored";

  if (!p) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: FONT }}>
        <p style={{ fontSize: 14, color: C.muted }}>No program data available</p>
      </div>
    );
  }

  const totalDays = p.weeks.reduce((a, w) => a + w.days.length, 0);
  const completedDays = p.weeks.reduce((a, w) => a + w.days.filter((d) => d.completed).length, 0);
  const overallPct = totalDays > 0 ? (completedDays / totalDays) * 100 : 0;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT, maxWidth: 480, margin: "0 auto" }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        * { box-sizing:border-box; margin:0; padding:0; }
      `}</style>

      <div style={{ padding: "48px 20px 20px", background: C.dark, color: "#fff", animation: "fadeIn 0.5s ease", position: "relative" }}>
        {onLogout && (
          <button onClick={onLogout} title="Logout" style={{
            position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.1)",
            border: "none", borderRadius: 8, padding: "8px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
          {p.coach} · {p.totalWeeks}-Week Program
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2, marginBottom: 2 }}>{p.name}</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", marginBottom: 18 }}>{isUnanchored ? "Work at your own pace" : `Started ${p.startDate}`}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.1)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${overallPct}%`, height: "100%", background: "#fff", borderRadius: 3, transition: "width 0.5s ease" }} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{completedDays}/{totalDays}</div>
        </div>
      </div>

      {(() => {
        const cw = p.weeks[p.currentWeek - 1];
        if (!cw) return null;
        const cwCompleted = cw.days.filter((d) => d.completed).length;
        const todayWorkout = cw.days.find((d) => d.isToday);
        return (
          <div style={{ margin: "16px 20px", borderRadius: 14, padding: "18px", background: C.chip, border: `1px solid ${C.borderLight}`, animation: "slideUp 0.4s ease 0.1s both" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>This Week</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{cw.label} — {cw.phase}</div>
              </div>
              <div style={{ padding: "4px 10px", borderRadius: 6, background: C.dark, color: "#fff", fontSize: 13, fontWeight: 700 }}>{cwCompleted}/{cw.days.length}</div>
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {cw.days.map((day, i) => (
                <div key={i} style={{ flex: 1, height: 6, borderRadius: 3, background: day.completed ? C.done : day.isToday ? C.dark : C.borderLight }} />
              ))}
            </div>
            {todayWorkout && (
              <div className="today-workout-tap" style={{
                display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                background: C.bg, borderRadius: 10, border: `1px solid ${C.borderLight}`, cursor: "pointer",
              }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: C.dark, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8H14M4 5V11M12 5V11M1 7V9M15 7V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{isUnanchored ? "Next:" : "Today:"} {todayWorkout.label}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{todayWorkout.focus} · {todayWorkout.exercises} exercises · ~{todayWorkout.estTime} min</div>
                </div>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ padding: "0 20px", animation: "slideUp 0.4s ease 0.2s both" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Full Program</div>

        {p.weeks.map((week) => {
          const isExpanded = expandedWeek === week.week;
          const weekCompleted = week.days.filter((d) => d.completed).length;
          const allDone = weekCompleted === week.days.length;
          const isCurrent = week.week === p.currentWeek;
          const isFuture = week.week > p.currentWeek;

          return (
            <div key={week.week} style={{ marginBottom: 6 }}>
              <button onClick={() => setExpandedWeek(isExpanded ? null : week.week)} style={{
                width: "100%", display: "flex", alignItems: "center", padding: "14px",
                background: isCurrent ? C.chip : C.bg,
                border: `1px solid ${isCurrent ? C.border : C.borderLight}`,
                borderRadius: isExpanded ? "12px 12px 0 0" : 12,
                cursor: "pointer", gap: 12, textAlign: "left", transition: "all 0.15s ease",
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 14, flexShrink: 0,
                  background: allDone ? C.done : isCurrent ? C.dark : "transparent",
                  border: allDone || isCurrent ? "none" : `2px solid ${isFuture ? C.borderLight : C.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: allDone || isCurrent ? "#fff" : C.muted, fontSize: 11, fontWeight: 700, fontFamily: FONT,
                }}>
                  {allDone ? <CheckIcon /> : week.week}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: isFuture ? C.muted : C.text, fontFamily: FONT }}>{week.label}</span>
                    {isCurrent && <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: C.dark, color: "#fff" }}>NOW</span>}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, fontFamily: FONT, marginTop: 1 }}>{week.phase} · {week.days.length} sessions</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <div style={{ display: "flex", gap: 2 }}>
                    {week.days.map((day, i) => (
                      <div key={i} style={{ width: 6, height: 6, borderRadius: 3, background: day.completed ? C.done : day.isToday ? C.dark : C.borderLight }} />
                    ))}
                  </div>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s ease" }}>
                    <path d="M4 6L8 10L12 6" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>

              {isExpanded && (
                <div style={{ border: `1px solid ${isCurrent ? C.border : C.borderLight}`, borderTop: "none", borderRadius: "0 0 12px 12px", overflow: "hidden", animation: "fadeIn 0.15s ease" }}>
                  {week.days.map((day, i) => (
                    <div
                      key={i}
                      className="day-card-clickable"
                      data-session-id={day.sessionId || `${week.week}-${day.day}`}
                      data-week={week.week}
                      data-day-index={i}
                      style={{
                        display: "flex", alignItems: "center", padding: "12px 14px", gap: 12,
                        background: day.isToday ? "rgba(17,17,17,0.03)" : C.bg,
                        borderBottom: i < week.days.length - 1 ? `1px solid ${C.borderLight}` : "none",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{
                        width: 24, height: 24, borderRadius: 12, flexShrink: 0,
                        background: day.completed ? C.done : day.isToday ? C.dark : "transparent",
                        border: day.completed || day.isToday ? "none" : `1.5px solid ${C.border}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: day.completed || day.isToday ? "#fff" : C.muted, fontSize: 10, fontWeight: 700, fontFamily: FONT,
                      }}>
                        {day.completed ? <CheckIcon /> : day.isToday ? "!" : day.day}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{
                            fontSize: 14, fontWeight: day.isToday ? 700 : 500,
                            color: day.completed ? C.muted : C.text,
                            textDecoration: day.completed ? "line-through" : "none", fontFamily: FONT,
                          }}>{day.label}</span>
                          {day.isToday && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: C.done, color: "#fff" }}>{isUnanchored ? "NEXT" : "TODAY"}</span>}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, fontFamily: FONT, marginTop: 1 }}>
                          {day.focus} · {day.exercises} exercises · {day.sets} sets · ~{day.estTime} min
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0, minWidth: 70 }}>
                        {isUnanchored ? (
                          <div style={{ fontSize: 11, color: day.completed ? C.done : C.muted, fontFamily: FONT, fontWeight: 500 }}>
                            {day.completed ? "✓ Done" : day.isToday ? "Up next" : ""}
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: 11, color: C.muted, fontFamily: FONT }}>{day.date}</div>
                            {day.time && <div style={{ fontSize: 10, color: C.muted, fontFamily: FONT, marginTop: 2 }}>{day.time}</div>}
                          </>
                        )}
                      </div>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke={C.borderLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ height: 48 }} />
    </div>
  );
}
